import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Lock, Server, Eye, FileText, 
  CheckCircle, AlertTriangle, Users, Database, Key, Heart,
  Trash2, Ban, UserCheck, ClipboardList
} from 'lucide-react';

export default function HIPAACompliance() {
  const navigate = useNavigate();

  const securityFeatures = [
    {
      icon: Lock,
      title: 'HTTPS/SSL Encryption',
      description: 'All data transmitted between your device and our servers is encrypted using TLS 1.3. Your connection is always secure.',
      status: 'implemented'
    },
    {
      icon: Database,
      title: 'Encrypted Database Storage',
      description: 'All data is stored in encrypted databases on reputable cloud infrastructure with automatic backups and industry-standard physical and network security.',
      status: 'implemented'
    },
    {
      icon: Key,
      title: 'Secure Authentication',
      description: 'Passwords are hashed using industry-standard bcrypt algorithm and are never stored in plain text.',
      status: 'implemented'
    },
    {
      icon: Users,
      title: 'Role-Based Access Control',
      description: 'Therapists control what information parents can see. Each user only accesses data relevant to their role.',
      status: 'implemented'
    },
    {
      icon: Eye,
      title: 'Data Minimisation',
      description: 'We collect only the minimum data necessary. We actively discourage entering identifiable personal information.',
      status: 'implemented'
    },
    {
      icon: Trash2,
      title: 'Data Deletion Rights',
      description: 'Therapists can permanently delete child profiles and all associated notes at any time. Data is fully removed from our systems.',
      status: 'implemented'
    }
  ];

  const dataMinimisationRules = [
    { do: 'Use first name only', dont: 'Full names or surnames' },
    { do: 'Age or age range', dont: 'Date of birth or ID numbers' },
    { do: 'General descriptions', dont: 'Home or school addresses' },
    { do: 'Communication style notes', dont: 'School names or teacher names' },
    { do: 'Clinical observations', dont: 'Medical record numbers' }
  ];

  const dataCategories = [
    {
      category: 'Therapist Account',
      data: 'Email address, name, professional registration number (optional)',
      purpose: 'Account management and authentication',
      retention: 'Until account deletion'
    },
    {
      category: 'Child Profile',
      data: 'First name only, age, communication style, interests, strengths',
      purpose: 'Personalise therapy activities and track developmental progress',
      retention: 'Until deleted by therapist or account termination'
    },
    {
      category: 'Session Notes (SOAP)',
      data: 'Clinical observations, therapy activities, goals addressed, progress notes',
      purpose: 'Document sessions, track progress, generate reports',
      retention: 'Until deleted by therapist or account termination'
    },
    {
      category: 'Language Samples',
      data: 'Recorded utterances, gestalts, communication examples',
      purpose: 'Track language development and inform therapy planning',
      retention: 'Until deleted by therapist or account termination'
    },
    {
      category: 'Assessment Data',
      data: 'Standardised test scores, clinical impressions, recommendations',
      purpose: 'Formal documentation and report generation',
      retention: 'Until deleted by therapist or account termination'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="h-6 w-6 text-primary" />
              <span className="font-serif text-xl text-primary-900">PlayFirst</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-4">
              Privacy & Data Protection
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              PlayFirst is committed to protecting client privacy through data minimisation, 
              secure storage, and clear responsibilities between the platform and clinicians.
            </p>
          </div>

          {/* Important Notice */}
          <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border-amber-200 mb-8" data-testid="important-notice">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-amber-900 mb-2">Important: Platform Purpose</h2>
                <p className="text-sm text-amber-800">
                  PlayFirst does not provide clinical services directly and acts solely as a 
                  <strong> documentation and productivity tool for qualified professionals</strong>. 
                  Clinicians are responsible for ensuring they have appropriate consent before entering client information.
                </p>
              </div>
            </div>
          </Card>

          {/* Data Minimisation */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="data-minimisation">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Ban className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Data Minimisation Policy</h2>
                <p className="text-sm text-stone-500">Protecting client privacy by design</p>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 mb-6">
              <p className="text-blue-800 font-medium">
                Do not enter identifying information beyond first name.
              </p>
              <p className="text-sm text-blue-700 mt-1">
                PlayFirst is designed to work with minimal personal data. Avoid entering any information 
                that could identify a child outside of the therapy context.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Safe to Enter
                </h3>
                <ul className="space-y-2 text-sm text-green-700">
                  {dataMinimisationRules.map((rule, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      {rule.do}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-4 bg-red-50 rounded-xl">
                <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4" /> Never Enter
                </h3>
                <ul className="space-y-2 text-sm text-red-700">
                  {dataMinimisationRules.map((rule, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                      {rule.dont}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>

          {/* Therapist Responsibilities */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="therapist-responsibilities">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Therapist Responsibilities</h2>
                <p className="text-sm text-stone-500">Your obligations when using PlayFirst</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-medium text-purple-900 mb-2">Consent Requirement</h3>
                <p className="text-sm text-purple-800">
                  Before entering any client information, you must obtain informed consent from the 
                  parent or legal guardian. When creating a child profile, you will confirm that 
                  consent has been obtained.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-xl">
                  <h4 className="font-medium text-stone-800 mb-2">You Must:</h4>
                  <ul className="text-sm text-stone-600 space-y-1">
                    <li>• Obtain parental consent before entering data</li>
                    <li>• Use first names only in profiles</li>
                    <li>• Keep your login credentials secure</li>
                    <li>• Log out on shared devices</li>
                    <li>• Delete profiles when no longer needed</li>
                  </ul>
                </div>
                <div className="p-4 bg-stone-50 rounded-xl">
                  <h4 className="font-medium text-stone-800 mb-2">You Must Not:</h4>
                  <ul className="text-sm text-stone-600 space-y-1">
                    <li>• Share login credentials</li>
                    <li>• Enter identifying information</li>
                    <li>• Store data without consent</li>
                    <li>• Share client data outside the app</li>
                    <li>• Use the app for non-therapy purposes</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* SOAP Notes Privacy */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="soap-notes-privacy">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Session Notes (SOAP Format)</h2>
                <p className="text-sm text-stone-500">Privacy guidelines for clinical documentation</p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Reminder:</strong> Please avoid surnames or identifiable personal details in your session notes.
                Focus on clinical observations, therapy techniques, and progress toward goals.
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-stone-50 rounded-xl">
                <h4 className="font-medium text-stone-800">Subjective (S)</h4>
                <p className="text-sm text-stone-600">Parent reports, child's mood - no identifying family details</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-xl">
                <h4 className="font-medium text-stone-800">Objective (O)</h4>
                <p className="text-sm text-stone-600">Clinical observations, data collected - describe behaviours, not locations</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-xl">
                <h4 className="font-medium text-stone-800">Assessment (A)</h4>
                <p className="text-sm text-stone-600">Clinical interpretation, progress - focus on therapeutic outcomes</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-xl">
                <h4 className="font-medium text-stone-800">Plan (P)</h4>
                <p className="text-sm text-stone-600">Next steps, recommendations - therapy-focused, not personal details</p>
              </div>
            </div>
          </Card>

          {/* Security Features */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="security-features">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Security Measures</h2>
                <p className="text-sm text-stone-500">How we protect your data</p>
              </div>
            </div>
            
            <div className="grid gap-4">
              {securityFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-stone-50 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-primary-900">{feature.title}</h3>
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                        Active
                      </span>
                    </div>
                    <p className="text-sm text-stone-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Data Collection */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="data-collection">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Data We Collect</h2>
                <p className="text-sm text-stone-500">Transparency about stored information</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {dataCategories.map((item, index) => (
                <div key={index} className="p-4 bg-stone-50 rounded-2xl">
                  <h3 className="font-medium text-primary-900 mb-2">{item.category}</h3>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-stone-500">Data</p>
                      <p className="text-stone-700">{item.data}</p>
                    </div>
                    <div>
                      <p className="text-stone-500">Purpose</p>
                      <p className="text-stone-700">{item.purpose}</p>
                    </div>
                    <div>
                      <p className="text-stone-500">Retention</p>
                      <p className="text-stone-700">{item.retention}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Legal Basis */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="legal-basis">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Legal Basis for Processing</h2>
                <p className="text-sm text-stone-500">Why we process your data</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-medium text-indigo-900 mb-2">Legitimate Professional Use</h3>
                <p className="text-sm text-indigo-800">
                  Data is processed to enable licensed speech-language therapists to deliver and document 
                  therapy services effectively.
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-medium text-indigo-900 mb-2">Consent Obtained by Therapist</h3>
                <p className="text-sm text-indigo-800">
                  Therapists confirm they have obtained consent from parents/guardians before entering 
                  any client information into PlayFirst.
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-medium text-indigo-900 mb-2">Contract Performance</h3>
                <p className="text-sm text-indigo-800">
                  Processing is necessary to provide the documentation and practice management services 
                  that therapists have subscribed to.
                </p>
              </div>
            </div>
          </Card>

          {/* Your Rights */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="your-rights">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif text-primary-900">Your Data Rights</h2>
                <p className="text-sm text-stone-500">Control over your information</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-800 mb-2">Therapists Can:</h3>
                <ul className="text-sm text-stone-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Delete any child profile permanently
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Delete individual session notes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Export their data
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Close their account
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-800 mb-2">Parents Can:</h3>
                <ul className="text-sm text-stone-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    View shared information only
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Request data deletion via therapist
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Withdraw from the platform
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl border-primary/20" data-testid="contact">
            <div className="text-center">
              <h2 className="text-xl font-serif text-primary-900 mb-2">Questions About Privacy?</h2>
              <p className="text-stone-600 mb-4">
                If you have any questions about our privacy practices or need to report a concern, 
                please contact us.
              </p>
              <Button 
                onClick={() => navigate('/settings')}
                className="rounded-full"
              >
                Contact Support
              </Button>
            </div>
          </Card>

          {/* Related Links */}
          <div className="text-center mt-8 space-y-2">
            <p className="text-sm text-stone-500">
              See also:{' '}
              <button onClick={() => navigate('/terms')} className="text-primary hover:underline">
                Terms of Service
              </button>
              {' | '}
              <button onClick={() => navigate('/refund-policy')} className="text-primary hover:underline">
                Refund Policy
              </button>
            </p>
          </div>

          {/* Last Updated */}
          <p className="text-center text-sm text-stone-400 mt-4">
            Last updated: February 2026
          </p>
        </motion.div>
      </main>
    </div>
  );
}
